import { useTransactionStats } from '../../../hooks/queries/useTransactionStats';
import { PATHS } from '../../../paths';
import { usePathWithDateRange } from '../../../providers/DateRangeProvider';
import { currentAmountForStream } from '../../../utils/spending/transactionStats';

import RecurringStreamSectionBase from './RecurringStreamSectionBase';

import type { RecurringStreamSectionFragment } from './__generated__/RecurringStreamSectionBase.gql';

export interface IRecurringStreamSectionProps {
  recurringTransaction: RecurringStreamSectionFragment;
  /**
   * The record's amount as the API serializes it — the transaction's or bill's
   * own, or the stream's last payment on the stream page. `null` when there is
   * none, which leaves the rows without a bar.
   */
  amount: string | null;
  className?: string;
}

/**
 * `RecurringStreamSectionBase` wired to `getTransactionStats` for the stream:
 * all-time over its own payments, the stream's type picking the sign. The
 * "View merchant" link carries the pane's current date range when there is
 * one, as the other merchant links do. A failed read only costs the bar — the
 * rows come from the fragment, so the section stays.
 */
const RecurringStreamSection = ({ recurringTransaction, amount, className }: IRecurringStreamSectionProps) => {
  const { id, type, merchant } = recurringTransaction;
  const { stats, loading } = useTransactionStats({
    kind: 'recurringTransaction',
    recurringTransactionId: id,
    streamType: type,
  });
  const addDateRangeToPath = usePathWithDateRange();

  return (
    <RecurringStreamSectionBase
      recurringTransaction={recurringTransaction}
      stats={stats}
      loading={loading}
      currentAmount={currentAmountForStream(amount, type)}
      viewMerchantPath={merchant ? addDateRangeToPath(PATHS.MERCHANT(merchant.id)) : null}
      className={className}
    />
  );
};

export default RecurringStreamSection;
